import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

interface ImageUploadProps {
  currentImage?: string;
  onImageUploaded: (imageUrl: string) => void;
  uploadEndpoint: string;
  entityId?: string;
  accept?: string;
  maxSize?: number;
  label?: string;
  className?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  onImageUploaded,
  uploadEndpoint,
  entityId,
  accept = "image/*",
  maxSize = 5,
  label = "Upload Image",
  className = "",
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > maxSize * 1024 * 1024) {
      alert(`File size must be less than ${maxSize}MB`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();

      // Always use 'image' as the field name - this matches our backend expectation
      formData.append('image', file);

      const token = localStorage.getItem('admin_token');

      // Construct the URL properly
      let url = `${API_BASE_URL}/upload/${uploadEndpoint}`;

      // If entityId is provided and not already in the uploadEndpoint, replace :id placeholder
      if (entityId && uploadEndpoint.includes(':id')) {
        url = url.replace(':id', entityId);
      }

      console.log('Uploading to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();

      // Fix: Use result.url instead of result.imageData.url
      const imageUrl = result.url || result.path;
      onImageUploaded(imageUrl);

      console.log('Upload successful:', result);
      alert('Image uploaded successfully!');
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(`Failed to upload image: ${error.message}`);
      setPreview(currentImage || null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className="flex items-center space-x-4">
        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border border-gray-300"
            />
            {!disabled && (
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
        )}

        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id={`image-upload-${uploadEndpoint.replace(/[^a-zA-Z0-9]/g, '-')}`}
            disabled={disabled || uploading}
          />

          <label
            htmlFor={`image-upload-${uploadEndpoint.replace(/[^a-zA-Z0-9]/g, '-')}`}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${uploading || disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Choose File'}
          </label>

          <p className="text-xs text-gray-500">
            Max size: {maxSize}MB. Supported: JPG, PNG, WebP
          </p>

          {currentImage && (
            <p className="text-xs text-gray-400 break-all">
              Current: {currentImage.length > 50 ? currentImage.substring(0, 50) + '...' : currentImage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;